/**
 * Los datos maestros que en la vida real llegan de ARMUS.
 *
 * Ninguno de estos se configura en Evoluciona: los escribe `syncFields()`
 * desde Odoo, con las entidades declaradas en `integration_entities`. Ningún
 * seeder los toca. Son, con su `source_key` en el mapeo:
 *
 *   estados_proyecto  <- estados          tipos de producto <- Tipo_productos
 *   tipos de proyecto <- tipos_proyecto   tipos de servicio <- tipo_servicios
 *   países            <- paises           unidades          <- unidad_negocios
 *   regiones          <- regiones         monedas           <- monedas
 *
 * Las únicas dos listas de acá que SÍ son de Evoluciona son las sociedades
 * (`project_companies`, con su seeder) y los roles de responsable.
 *
 * Estos valores son un relleno para poder recorrer las pantallas en local sin
 * ARMUS. Están en un solo archivo a propósito: escritos por separado en cada
 * pantalla, dos listas de regiones terminan diciendo cosas distintas.
 *
 * Los ids son los que lleva el proyecto en su columna, que es lo que compara
 * el filtro por alcance:
 *   sociedad       -> projects.id_project_company
 *   unidad         -> projects.business_unit_odoo_id  (business_units.id_odoo)
 *   región         -> projects.id_region
 */
export interface Maestro {
  /** El código de ARMUS, cuando el catálogo lo tiene */
  codigo?: string;
  id: number;
  nombre: string;
}

export const SOCIEDADES: Maestro[] = [
  { id: 1, nombre: 'Vippinn Argentina' },
  { id: 2, nombre: 'Vippinn Chile' },
  { id: 3, nombre: 'Vippinn USA' }
];

export const UNIDADES: Maestro[] = [
  { id: 10, nombre: 'Consultoría' },
  { id: 20, nombre: 'Desarrollo' }
];

export const REGIONES: Maestro[] = [
  { id: 1, nombre: 'Argentina' },
  { id: 2, nombre: 'Chile' },
  { id: 3, nombre: 'Estados Unidos' }
];

export const TIPOS_PROYECTO: Maestro[] = [
  { id: 1, nombre: 'Productivo' },
  { id: 2, nombre: 'Estructura' }
];

/* Los siete estados reales de ARMUS, con sus ids reales.

   Eran tres inventados —«En análisis», «En ejecución», «Cerrado»— y eso hacía
   que la barra de estados del preview se viera distinta de la de DEV sin que
   nada estuviera roto: la diferencia era el dato de demo, no el código.

   **Ojo con los ids: no acompañan a la secuencia.** Replanificación es el 6 y
   va cuarta; Finalizado es el 4 y va quinta. Es a propósito: si el preview los
   tuviera ordenaditos del 1 al 7, un bug que ordene por id pasaría inadvertido
   acá y aparecería recién en DEV. */
export const ESTADOS_PROYECTO: Maestro[] = [
  { id: 1, codigo: 'planificacion', nombre: 'Planificación' },
  { id: 2, codigo: 'en_curso', nombre: 'En Curso' },
  { id: 3, codigo: 'en_pausa', nombre: 'En Pausa' },
  { id: 6, codigo: 'replanificacion', nombre: 'Replanificación' },
  /* El typo es de ARMUS y viaja tal cual: corregirlo acá haría que el preview
     ande y DEV no. */
  { id: 4, codigo: 'finalizaso', nombre: 'Finalizado' },
  { id: 9, codigo: 'cerrado', nombre: 'Cerrado' },
  { id: 5, codigo: 'cancelado', nombre: 'Cancelado' }
];

export const PAISES: Maestro[] = [
  { id: 1, nombre: 'Argentina' },
  { id: 2, nombre: 'Chile' },
  { id: 3, nombre: 'Estados Unidos' }
];

export const TIPOS_PRODUCTO: Maestro[] = [
  { id: 1, nombre: 'Servicios profesionales' },
  { id: 2, nombre: 'Licencias' }
];

/** Cuelgan de un tipo de producto: la clave es el id del producto */
export const TIPOS_SERVICIO: Record<number, Maestro[]> = {
  1: [
    { id: 1, nombre: 'Llave en mano' },
    { id: 2, nombre: 'Talen Hub' },
    { id: 3, nombre: 'Mantenimiento (AM)' }
  ],
  2: [{ id: 4, nombre: 'Suscripción' }]
};

export const ROLES_RESPONSABLE: Maestro[] = [
  { id: 1, nombre: 'Gestor de proyecto' },
  { id: 2, nombre: 'Responsable de mercado' }
];

export const MONEDAS: Maestro[] = [
  { id: 1, nombre: 'USD' },
  { id: 2, nombre: 'ARS' }
];

/**
 * Como los devuelve /api/system/*.
 *
 * Ojo: no todos los catalogos se leen por la misma clave. Sociedades y regiones
 * viven en tablas propias de Evoluciona y las pantallas las recorren por `id`;
 * el resto llega de Odoo y se recorre por `id_odoo`. Sembrar todo con la misma
 * clave dejaba los desplegables de Empresa y Region vacios.
 */
export function comoCatalogo(items: Maestro[]): { id_odoo: number; codigo?: string; description: string }[] {
  return items.map((i) => ({ id_odoo: i.id, codigo: i.codigo, description: i.nombre }));
}

export function comoCatalogoPorId(items: Maestro[]): { id: number; description: string }[] {
  return items.map((i) => ({ id: i.id, description: i.nombre }));
}
